/**
 * Hold session ids — the ONE thing that proves a seat hold, an inventory hold
 * or a server cart belongs to the caller.
 *
 * Release, extend, remove-from-cart, clear and checkout all act on whatever
 * session id the request presents, and the browser (or a partner's server)
 * mints it. So the id is a secret, and three rules keep it one:
 *
 *  1. It is unguessable: 128 bits from the platform CSPRNG
 *     (`newHoldSessionId`). The storefront's old id was
 *     `seat-<ms>-<7 chars of Math.random>`.
 *  2. It never leaves the server in a public read. The seat-map bundle and the
 *     status feed used to carry every held seat's `holdSessionId`, so anyone
 *     could copy a stranger's id and release (or extend, or buy) their seats.
 *     A buyer now learns which held seats are theirs from `mine`, computed
 *     from their OWN session (the hold answer, /seats/my-holds).
 *  3. Server-assigned namespaces can never be presented by a public caller:
 *     admin holds (COMP / ARTIST / ...) are written as `admin:<userId>`, and a
 *     public route that accepted that string would release, extend or sell
 *     them.
 *
 * Isomorphic on purpose (no Node imports): the storefront mints with it and
 * the routes refuse with it.
 */

/** Session-id namespaces only the server writes. */
export const RESERVED_HOLD_SESSION_PREFIXES = ['admin:'] as const

/** True for an id a public caller must never act as (see rule 3). */
export function isReservedHoldSessionId(id: unknown): boolean {
  if (typeof id !== 'string') return false
  const lower = id.trim().toLowerCase()
  return RESERVED_HOLD_SESSION_PREFIXES.some(p => lower.startsWith(p))
}

/**
 * A fresh hold session id: `<prefix>-<32 hex chars>` (128 random bits).
 * The prefix only says which surface minted it (`seat`, `bo-seat`, ...).
 */
export function newHoldSessionId(prefix: string): string {
  const bytes = new Uint8Array(16)
  globalThis.crypto.getRandomValues(bytes)
  let hex = ''
  for (const b of bytes) hex += b.toString(16).padStart(2, '0')
  return `${prefix}-${hex}`
}

/**
 * The storefront's pre-2026-11 seat session id: a millisecond timestamp plus
 * ~36 bits of Math.random. Kept only while the browser's cart still holds this
 * event's tickets under it (those holds and that server cart are keyed by it).
 */
export function isLegacyHoldSessionId(id: unknown): boolean {
  return typeof id === 'string' && /^seat-\d{10,14}-[a-z0-9]{1,12}$/.test(id)
}
